import isEqual from 'lodash/isEqual';
import { io } from 'socket.io-client';
import cloneDeep from 'lodash/cloneDeep';

import socketServerUrl from 'api/Socket';

import isBinary from 'lib/helpers/isBinary';

const eventRegistry = new Map();
const reconnectEmitRegistry = new Set();
let autoRehydrate = true;
let connectedOnce = false;
let pendingEmitAfterReconnect = false;
let reconnectExhausted = false;
let recoveryListenersRegistered = false;
let lifecycleListenersRegistered = false;

const socket = io(socketServerUrl, {
  autoConnect: false,
  withCredentials: true,
  transports: ['websocket'],
  reconnection: true,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 30000,
  reconnectionAttempts: 10
});

socket.startedAuth = false;

/**
 * Registers an event to the current socket, replacing any handler previously registered through
 * this function. The listener stays attached across reconnects.
 * @param {string} event - Event that you want to listen to, e.g. appointment-open.
 * @param {Function} handler - Callback handler when socket sends us this event.
 */
function socketOn(event, handler) {
  const existingHandler = eventRegistry.get(event);
  if (existingHandler) {
    socket.off(event, existingHandler);
  }

  socket.on(event, handler);
  eventRegistry.set(event, handler);
}

/**
 * Removes the event listener and it's handle from the current socket, and forgets it as the event's registered handler.
 * @param {string} event - Event that you want to remove to, e.g. appointment-open.
 * @param {callback function} handler - Callback handler when socket sends us this event.
 */
function socketOff(event, handler) {
  socket.off(event, handler);

  const currentHandler = eventRegistry.get(event);
  if (currentHandler === handler) {
    eventRegistry.delete(event);
  }
}

/**
 * Emits an event to the server socket. In case of the true reemitOnReconnect, re-emits the event in case of reconnect e.g. events that make the server socket join a room
 * @param {string} event - Event that you want to emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 * @param {{ reemitOnReconnect: boolean; }} [options={ reemitOnReconnect: false }] - Flag to add the event to the reemit registry
 */
function socketEmit(event, payload, options = { reemitOnReconnect: false }) {
  try {
    const safePayload = isBinary(payload) ? payload : cloneDeep(payload);
    JSON.stringify(safePayload);
    socket.emit(event, safePayload);

    if (options.reemitOnReconnect && autoRehydrate) {
      let alreadyInSet = false;
      for (const item of reconnectEmitRegistry) {
        if (item.event === event && isEqual(item.payload, safePayload)) {
          alreadyInSet = true;
          break;
        }
      }
      if (!alreadyInSet) {
        reconnectEmitRegistry.add({ event, payload: safePayload });
      }
    }
  } catch (error) {
    console.error(`Emit failed for event "${event}":`, error);
  }
}

/**
 * Cancels the re-emitting the event. This method needs to be used to remove the event in case
 * its no longer needed, e.g. socket leaves a room, you cancel the reemit for the event used to join the room.
 * @param {string} event - Event that you want to emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 */
function cancelReEmitFor(event, payload) {
  for (const entry of reconnectEmitRegistry) {
    if (entry.event === event && isEqual(entry.payload, payload)) {
      reconnectEmitRegistry.delete(entry);
      break;
    }
  }
}

/**
 * Method used to emit the rest of the events from the event registry of the socket, since we should wait for the
 * first event that initializes the socket and handles authentication to not have a false non-authenticated handles on the sever for the events
 * @param {string} event - Event that you want to cancel the re-emit to, e.g. join-clinical.
 * @param {object} payload - Payload to be sent together with the event.
 */
function emitRest() {
  if (pendingEmitAfterReconnect) {
    pendingEmitAfterReconnect = false;
    let isFirst = true;
    for (const { event, payload } of reconnectEmitRegistry) {
      if (isFirst) {
        isFirst = false;
        continue;
      }
      socketEmit(event, payload, { reemitOnReconnect: true });
    }
  }
}

/**
 * Function needed to restore the socket to it's former state before the reconnect
 */
function reinitializeAfterReconnect() {
  if (!autoRehydrate) return;

  // Listeners are NOT re-registered here: they survive a reconnect on the same socket instance, so
  // replaying the registry stacked one more copy of every handler per reconnect.
  const iterator = reconnectEmitRegistry.values();
  const first = iterator.next().value;
  if (first) socketEmit(first.event, first.payload);
  if (reconnectEmitRegistry.size > 1) {
    pendingEmitAfterReconnect = true;
  }
}

/**
 * Function that disables the restoration of the socket state after a reconnect
 */
function setRehydration(enabled) {
  autoRehydrate = enabled;

  if (!enabled) {
    reconnectEmitRegistry.clear();
  }
}

function retryConnectionIfExhausted() {
  if (reconnectExhausted && !socket.connected) {
    reconnectExhausted = false;
    socket.connect();
  }
}

/**
 * General function to initialize a socket connection, everything else gets build up from this part.
 */
function initSocket() {
  socket.connect();

  // Once per page load: initSocket runs again on every re-login, and a second `connect` handler
  // would run the reconnect restore twice.
  if (!lifecycleListenersRegistered) {
    lifecycleListenersRegistered = true;

    socket.io.on('reconnect_failed', () => {
      reconnectExhausted = true;
      console.warn('Socket reconnection failed after max attempts');
    });

    socket.on('connect', () => {
      reconnectExhausted = false;
      if (!connectedOnce) {
        connectedOnce = true;
      } else {
        reinitializeAfterReconnect();
      }
    });

    socket.on('disconnect', (reason) => {
      console.warn('Socket disconnected:', reason);
    });

    socket.on('connect_error', (err) => {
      console.warn('Connection error:', err);
    });
  }

  if (!recoveryListenersRegistered && typeof window !== 'undefined') {
    window.addEventListener('online', retryConnectionIfExhausted);
    window.addEventListener('focus', retryConnectionIfExhausted);
    recoveryListenersRegistered = true;
  }
}

/**
 * Stable per-tab id sent with every `user-auth` emit. The backend stores it on the socket
 * registry row and only notifies sockets that have one, so an emit without it costs that
 * session every permission-gated notification until it reconnects.
 * @returns {string} the current tab's session client id, created on first use
 */
function getSessionClientId() {
  let sessionClientId = sessionStorage.getItem('sessionClientId');

  if (!sessionClientId) {
    sessionClientId = crypto.randomUUID().replace(/-/g, '');
    sessionStorage.setItem('sessionClientId', sessionClientId);
  }

  return sessionClientId;
}

export {
  cancelReEmitFor,
  emitRest,
  getSessionClientId,
  initSocket,
  reinitializeAfterReconnect,
  setRehydration,
  socket,
  socketEmit,
  socketOff,
  socketOn
};
